import React from "react";
import { Loader2, Send, ShieldCheck } from "lucide-react";
import { useInfobip } from "./infobip/useInfobip";
import { InfobipGuard } from "./infobip/InfobipGuard";
import { InfobipDnsMatrix } from "./infobip/InfobipDnsMatrix";
import { InfobipTestRow } from "./infobip/InfobipTestRow";

/**
 * Per-site Infobip email connection panel with full DNS auto-onboarding.
 * Uses ONE global Infobip account (creds in backend env) — no per-site key.
 *   • Empty → "Connect & auto-configure DNS"
 *   • Connected → DNS matrix (Cloudflare vs Infobip) + Fix DNS / Verify / test.
 */
export default function InfobipPanel({ site, reload }) {
  const ib = useInfobip(site, reload);
  const guard = InfobipGuard({ site, status: ib.status });
  if (guard) return guard;

  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5 space-y-4" data-testid="infobip-panel">
      <div className="flex items-center gap-3">
        <Send size={18} className="text-sky-400" />
        <div className="font-display text-lg font-bold">Infobip transactional email</div>
        {ib.connected && <span className="dot bg-sky-500 ml-auto" data-testid="infobip-connected-dot" />}
      </div>

      {!ib.connected && (
        <>
          <div className="text-sm text-zinc-400 leading-relaxed">
            Register <span className="mono text-zinc-200">{site.domain}</span> as an Infobip sending domain. We&apos;ll create it in Infobip, publish every DNS record it returns (SPF, DKIM, tracking, MX) into Cloudflare automatically, then trigger verification. Uses your global Infobip account — no per-site key.
          </div>
          <button
            data-testid="infobip-connect-btn"
            onClick={ib.connect}
            disabled={ib.busy}
            className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-50"
          >
            {ib.busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            Connect &amp; auto-configure DNS
          </button>
        </>
      )}

      {ib.connected && (
        <>
          <InfobipDnsMatrix {...ib} />
          <InfobipTestRow busy={ib.busy} sendTest={ib.sendTest} disconnect={ib.disconnect} />
        </>
      )}

      {ib.msg && <div className="text-xs text-sky-400 mono break-all" data-testid="infobip-msg">{ib.msg}</div>}
      {ib.err && <div className="text-xs text-red-400 mono break-all" data-testid="infobip-err">{ib.err}</div>}
    </div>
  );
}
